import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

function ReelRow({ images, reverse = false, duration = 60, onOpen, offset = 0 }) {
  const doubled = [...images, ...images];
  return (
    <div className="group relative overflow-hidden edge-fade-x" data-testid={reverse ? "art-reel-row-b" : "art-reel-row-a"}>
      <div
        className="aa-reel-track flex w-max gap-4 py-2"
        style={{ animation: `marquee ${duration}s linear infinite ${reverse ? "reverse" : ""}` }}
      >
        {doubled.map((src, i) => (
          <button
            key={`${src}-${i}`}
            data-testid={`reel-item-${reverse ? "b" : "a"}-${i % images.length}`}
            onClick={() => onOpen((offset + (i % images.length)))}
            className="shrink-0 overflow-hidden border border-white/10 hover:border-arsenal/70 transition-colors duration-300 focus:outline-none focus:border-arsenal"
            aria-label="Open artwork full size"
          >
            <img
              src={src}
              alt=""
              loading="lazy"
              draggable={false}
              className="h-44 sm:h-60 w-auto object-cover transition-transform duration-500 hover:scale-[1.04]"
            />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function ArtReel({ images, title }) {
  const [open, setOpen] = useState(null);

  const close = useCallback(() => setOpen(null), []);
  const step = useCallback(
    (d) => setOpen((o) => (o === null ? o : (o + d + images.length) % images.length)),
    [images.length]
  );

  useEffect(() => {
    if (open === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close, step]);

  if (!images?.length) return null;
  const mid = Math.ceil(images.length / 2);
  const rows = images.length > 5
    ? [{ imgs: images.slice(0, mid), reverse: false, offset: 0, dur: 70 }, { imgs: images.slice(mid), reverse: true, offset: mid, dur: 58 }]
    : [{ imgs: images, reverse: false, offset: 0, dur: 60 }];

  return (
    <section className="py-6" data-testid="art-reel">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-end justify-between gap-6 mb-6">
        <p className="font-mono text-[11px] sm:text-xs tracking-[0.35em] text-arsenal">THE REEL</p>
        <p className="font-mono text-[10px] tracking-[0.2em] text-slate-600 hidden sm:block">HOVER TO PAUSE — CLICK TO INSPECT</p>
      </div>
      <div className="space-y-4">
        {rows.map((r, i) => (
          <ReelRow key={i} images={r.imgs} reverse={r.reverse} duration={r.dur} offset={r.offset} onOpen={setOpen} />
        ))}
      </div>

      <AnimatePresence>
        {open !== null && (
          <motion.div
            data-testid="art-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-black/92 backdrop-blur-md p-5"
            onClick={close}
            role="dialog"
            aria-label={`${title} — artwork ${open + 1} of ${images.length}`}
          >
            <button
              data-testid="lightbox-close"
              onClick={close}
              className="absolute top-5 right-5 text-slate-400 hover:text-arsenal transition-colors p-2"
              aria-label="Close"
            >
              <X className="h-7 w-7" />
            </button>
            <button
              data-testid="lightbox-prev"
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              className="absolute left-3 sm:left-8 text-slate-400 hover:text-arsenal transition-colors p-2"
              aria-label="Previous artwork"
            >
              <ChevronLeft className="h-9 w-9" />
            </button>
            <motion.img
              key={images[open]}
              src={images[open]}
              alt={`${title} — artwork ${open + 1}`}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[82vh] max-w-[88vw] object-contain border border-white/10 shadow-[0_0_80px_rgba(229,9,20,0.15)]"
              data-testid="lightbox-image"
            />
            <button
              data-testid="lightbox-next"
              onClick={(e) => { e.stopPropagation(); step(1); }}
              className="absolute right-3 sm:right-8 text-slate-400 hover:text-arsenal transition-colors p-2"
              aria-label="Next artwork"
            >
              <ChevronRight className="h-9 w-9" />
            </button>
            <p className="absolute bottom-5 left-1/2 -translate-x-1/2 font-mono text-[11px] tracking-[0.25em] text-slate-500">
              {open + 1} / {images.length}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
